/**
 * Ingestion Metrics Cards Component
 *
 * Displays KPI summary cards for document ingestion metrics
 */

import { MetricCard } from './MetricCard';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import { formatNumber } from '@/lib/formatters';
import type { IngestionSummary } from '@/types/dashboard';

interface IngestionMetricsCardsProps {
  data: IngestionSummary | null | undefined;
  isLoading: boolean;
}

export function IngestionMetricsCards({
  data,
  isLoading,
}: IngestionMetricsCardsProps) {
  // Show skeleton when loading OR when data hasn't arrived yet
  const showSkeleton = isLoading || data === null || data === undefined;

  return (
    <div
      className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      role="region"
      aria-label="Document ingestion metrics summary"
    >
      <MetricCard
        title="Docs Ingested"
        value={formatNumber(data?.totalDocsIngested)}
        description="Documents added to knowledge base"
        isLoading={showSkeleton}
        variant="success"
        tooltip={METRIC_DESCRIPTIONS.docsIngested}
      />
      <MetricCard
        title="Webhooks"
        value={formatNumber(data?.totalWebhooks)}
        description="Real-time webhook events received"
        isLoading={showSkeleton}
        tooltip={METRIC_DESCRIPTIONS.webhooks}
      />
      <MetricCard
        title="Sync Jobs"
        value={formatNumber(data?.totalSyncJobs)}
        description="Scheduled sync operations completed"
        isLoading={showSkeleton}
        tooltip={METRIC_DESCRIPTIONS.syncJobs}
      />
      <MetricCard
        title="Ingestion Errors"
        value={formatNumber(data?.totalIngestionErrors)}
        description="Failed ingestion operations"
        isLoading={showSkeleton}
        threshold={
          data?.totalIngestionErrors !== undefined
            ? {
                value: data.totalIngestionErrors,
                warningThreshold: 1,
                criticalThreshold: 10,
                inverse: true,
              }
            : undefined
        }
        tooltip={METRIC_DESCRIPTIONS.ingestionErrors}
      />
    </div>
  );
}
